import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { pickWorldPointFromPointer } from '../utils/viewerSpatialUtils';
import { type WorldPos } from '../utils/measureUtils';
import { customerApi, type AssetLibraryLabel } from '../services/customerApi';
import SceneLabelsLayer, { type SceneLabelItem } from './sceneInteractions/SceneLabelsLayer';

interface LabelToolProps {
  sceneId: string;
  viewerRef: React.MutableRefObject<any>;
  isActive: boolean;
  onClose: () => void;
  onLabelsChange?: (labels: SceneLabelItem[]) => void;
  anchorRef?: React.RefObject<HTMLButtonElement | null>;
}

interface LabelAnchor {
  id: string;
  text: string;
  worldPos: WorldPos;
  persisted: boolean;
}

type LabelStep = 'capturing' | 'editing' | 'done';

interface LabelDragState {
  id: string;
  pointerId: number;
  startX: number;
  startY: number;
  moved: boolean;
  ndcZ: number;
}

function worldFromTuple(world: [number, number, number]): WorldPos {
  return { x: world[0], y: world[1], z: world[2] };
}

function tupleFromWorld(world: WorldPos): [number, number, number] {
  return [
    Number(world.x.toFixed(4)),
    Number(world.y.toFixed(4)),
    Number(world.z.toFixed(4)),
  ];
}

function labelFromAsset(asset: AssetLibraryLabel): LabelAnchor {
  return {
    id: asset.id,
    text: asset.name,
    worldPos: worldFromTuple(asset.world),
    persisted: true,
  };
}

function syncViewerCameras(viewer: any) {
  if (!viewer?.camera) return;
  viewer.clickTransitioning = false;
  viewer.transitioningCameraTarget = false;
  if (viewer.nextCamera) viewer.nextCamera.copy(viewer.camera);
  if (viewer.previousCamera) viewer.previousCamera.copy(viewer.camera);
}

function worldFromPointerAtNdcDepth(
  viewer: any,
  clientX: number,
  clientY: number,
  ndcZ: number,
): WorldPos | null {
  const camera = viewer?.camera as THREE.Camera | undefined;
  const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
  if (!camera || !canvas) return null;

  const rect = canvas.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  camera.updateMatrixWorld(true);
  camera.matrixWorldInverse.copy(camera.matrixWorld).invert();

  const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
  const ndcY = -(((clientY - rect.top) / rect.height) * 2 - 1);
  const world = new THREE.Vector3(ndcX, ndcY, ndcZ).unproject(camera);
  if (!Number.isFinite(world.x) || !Number.isFinite(world.y) || !Number.isFinite(world.z)) {
    return null;
  }

  return { x: world.x, y: world.y, z: world.z };
}

function ndcDepthForWorld(viewer: any, worldPos: WorldPos): number | null {
  const camera = viewer?.camera as THREE.Camera | undefined;
  if (!camera) return null;

  camera.updateMatrixWorld(true);
  camera.matrixWorldInverse.copy(camera.matrixWorld).invert();

  const projected = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z).project(camera);
  if (!Number.isFinite(projected.z)) return null;
  return projected.z;
}

const LabelTool: React.FC<LabelToolProps> = ({
  sceneId,
  viewerRef,
  isActive,
  onClose,
  onLabelsChange,
  anchorRef,
}) => {
  const [step, setStep] = useState<LabelStep>('capturing');
  const [labels, setLabels] = useState<LabelAnchor[]>([]);
  const [draftText, setDraftText] = useState('');
  const [editText, setEditText] = useState('');
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  const [draggingLabelId, setDraggingLabelId] = useState<string | null>(null);
  const [pendingPoint, setPendingPoint] = useState<WorldPos | null>(null);
  const pointerDownRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const labelsRef = useRef<LabelAnchor[]>([]);
  const dragStateRef = useRef<LabelDragState | null>(null);
  const dragMoveEventRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const dragRafRef = useRef<number | null>(null);

  useEffect(() => {
    labelsRef.current = labels;
  }, [labels]);

  useEffect(() => {
    onLabelsChange?.(labels.map(({ id, text, worldPos }) => ({ id, text, worldPos })));
  }, [labels, onLabelsChange]);

  useEffect(() => {
    let cancelled = false;

    customerApi.getSceneAssetLabels(sceneId)
      .then((response) => {
        if (cancelled) return;
        const loaded = response.data.map(labelFromAsset);
        setLabels(loaded);
        if (loaded.length > 0) setStep('done');
      })
      .catch((error) => {
        if (!cancelled) console.warn('[LabelTool] Failed to load saved labels:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [sceneId]);

  useEffect(() => {
    if (!isActive) return;

    setStep(labelsRef.current.length > 0 ? 'done' : 'capturing');
    setPendingPoint(null);
    setSelectedLabelId(null);
    setEditText('');
    setDraggingLabelId(null);
    dragStateRef.current = null;

    const viewer = viewerRef.current;
    const previousMovementSpeed = viewer?.controls?.movementSpeed ?? 5;
    const previousLookSpeed = viewer?.controls?.lookSpeed ?? 10;
    const previousMaxClickDistance = viewer?.maxClickToMoveDistance ?? 15;

    if (viewer?.controls) {
      viewer.controls.movementSpeed = 0.3;
      viewer.controls.lookSpeed = 0;
    }
    if (viewer) {
      viewer.maxClickToMoveDistance = 0;
      syncViewerCameras(viewer);
    }

    return () => {
      const currentViewer = viewerRef.current;
      if (!currentViewer || currentViewer !== viewer) return;

      if (currentViewer.controls) {
        currentViewer.controls.movementSpeed = previousMovementSpeed;
        currentViewer.controls.lookSpeed = previousLookSpeed;
      }
      currentViewer.maxClickToMoveDistance = previousMaxClickDistance;
      syncViewerCameras(currentViewer);
    };
  }, [isActive, viewerRef]);

  const consumePointerEvent = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation?.();
  }, []);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    consumePointerEvent(event);
    if (step !== 'capturing') return;

    pointerDownRef.current = {
      x: event.clientX,
      y: event.clientY,
      pointerId: event.pointerId,
    };
  }, [consumePointerEvent, step]);

  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    consumePointerEvent(event);
    if (step !== 'capturing') return;

    const pointerDown = pointerDownRef.current;
    pointerDownRef.current = null;
    if (!pointerDown || pointerDown.pointerId !== event.pointerId) return;

    const moved =
      Math.abs(event.clientX - pointerDown.x) > 6 ||
      Math.abs(event.clientY - pointerDown.y) > 6;
    if (moved) return;

    const point = pickWorldPointFromPointer(viewerRef.current, event.clientX, event.clientY);
    if (!point) {
      console.warn('[LabelTool] Ray pick failed');
      return;
    }

    console.log('[LabelTool] Picked label point', {
      x: Number(point.x.toFixed(4)),
      y: Number(point.y.toFixed(4)),
      z: Number(point.z.toFixed(4)),
    });
    setPendingPoint(point);
    setDraftText('');
    setStep('editing');
  }, [consumePointerEvent, step, viewerRef]);

  const saveLabel = useCallback(async () => {
    if (!pendingPoint || !draftText.trim()) return;

    const labelText = draftText.trim();
    const world = tupleFromWorld(pendingPoint);
    console.log(
      `[LabelTool] Placing label "${labelText}" at world=[${world[0]}, ${world[1]}, ${world[2]}]`,
      world,
    );

    const tempId = `local-${Date.now()}`;
    setLabels((prev) => [
      ...prev,
      {
        id: tempId,
        text: labelText,
        worldPos: pendingPoint,
        persisted: false,
      },
    ]);
    setPendingPoint(null);
    setDraftText('');
    setStep('done');

    try {
      const response = await customerApi.createSceneAssetLabel(sceneId, {
        name: labelText,
        world,
        properties: { kind: 'label' },
      });
      setLabels((prev) => prev.map((label) => (
        label.id === tempId ? labelFromAsset(response.data) : label
      )));
    } catch (error) {
      console.warn('[LabelTool] Failed to save label:', error);
    }
  }, [draftText, pendingPoint, sceneId]);

  const selectLabel = useCallback((label: LabelAnchor) => {
    if (!isActive) return;
    setSelectedLabelId(label.id);
    setEditText(label.text);
    setStep('done');
  }, [isActive]);

  const handleLayerPointerDown = useCallback((label: SceneLabelItem, event: React.PointerEvent<HTMLDivElement>) => {
    if (!isActive) return;
    consumePointerEvent(event);

    const fullLabel = labelsRef.current.find((item) => item.id === label.id);
    if (!fullLabel) return;

    const ndcZ = ndcDepthForWorld(viewerRef.current, fullLabel.worldPos);
    if (ndcZ === null) return;

    dragStateRef.current = {
      id: fullLabel.id,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      ndcZ,
    };
  }, [consumePointerEvent, isActive, viewerRef]);

  const updateSelectedText = useCallback(async () => {
    const label = labels.find((item) => item.id === selectedLabelId);
    const nextText = editText.trim();
    if (!label || !nextText) return;

    setLabels((prev) => prev.map((item) => (
      item.id === label.id ? { ...item, text: nextText } : item
    )));
    if (!label.persisted) return;

    try {
      await customerApi.updateSceneAssetLabel(sceneId, label.id, { name: nextText });
    } catch (error) {
      console.warn('[LabelTool] Failed to update label text:', error);
    }
  }, [editText, labels, sceneId, selectedLabelId]);

  useEffect(() => {
    if (!isActive) {
      dragStateRef.current = null;
      setDraggingLabelId(null);
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      const drag = dragStateRef.current;
      if (!drag) return;

      const movedEnough =
        drag.moved ||
        Math.abs(event.clientX - drag.startX) > 4 ||
        Math.abs(event.clientY - drag.startY) > 4;
      if (!movedEnough) return;

      drag.moved = true;
      setDraggingLabelId(drag.id);
      dragMoveEventRef.current = { clientX: event.clientX, clientY: event.clientY };
      if (dragRafRef.current !== null) return;

      dragRafRef.current = requestAnimationFrame(() => {
        dragRafRef.current = null;
        const currentDrag = dragStateRef.current;
        const latestMove = dragMoveEventRef.current;
        if (!currentDrag || !latestMove) return;

        const label = labelsRef.current.find((item) => item.id === currentDrag.id);
        if (!label) return;

        const world = worldFromPointerAtNdcDepth(
          viewerRef.current,
          latestMove.clientX,
          latestMove.clientY,
          currentDrag.ndcZ,
        );
        if (!world) return;

        const updated = labelsRef.current.map((item) => (
          item.id === currentDrag.id ? { ...item, worldPos: world } : item
        ));
        labelsRef.current = updated;
        setLabels(updated);
      });
    };

    const finishDrag = async () => {
      const drag = dragStateRef.current;
      dragStateRef.current = null;
      dragMoveEventRef.current = null;
      if (dragRafRef.current !== null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
      setDraggingLabelId(null);
      if (!drag) return;

      const label = labelsRef.current.find((item) => item.id === drag.id);
      if (!label) return;

      if (!drag.moved) {
        selectLabel(label);
        return;
      }

      const world = tupleFromWorld(label.worldPos);
      console.log(`[LabelTool] Moving label "${label.text}" to world=[${world[0]}, ${world[1]}, ${world[2]}]`, world);
      if (!label.persisted) return;

      try {
        await customerApi.updateSceneAssetLabel(sceneId, label.id, { world });
      } catch (error) {
        console.warn('[LabelTool] Failed to move label:', error);
      }
    };

    const onPointerUp = () => {
      void finishDrag();
    };

    const onPointerCancel = () => {
      dragStateRef.current = null;
      dragMoveEventRef.current = null;
      if (dragRafRef.current !== null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
      setDraggingLabelId(null);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };
  }, [isActive, sceneId, selectLabel, viewerRef]);

  const deleteSelectedLabel = useCallback(async () => {
    const label = labels.find((item) => item.id === selectedLabelId);
    if (!label) return;

    setLabels((prev) => prev.filter((item) => item.id !== label.id));
    setSelectedLabelId(null);
    setEditText('');
    if (!label.persisted) return;

    try {
      await customerApi.deleteSceneAssetLabel(sceneId, label.id);
    } catch (error) {
      console.warn('[LabelTool] Failed to delete label:', error);
    }
  }, [labels, sceneId, selectedLabelId]);

  if (!isActive && labels.length === 0) return null;

  return (
    <>
      {step === 'capturing' && (
        <div
          className="fixed inset-0 z-[1001]"
          style={{ cursor: 'crosshair', pointerEvents: 'auto', background: 'transparent' }}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
        />
      )}

      {isActive && step === 'capturing' && <style>{`canvas { cursor: crosshair !important; }`}</style>}

      <SceneLabelsLayer
        viewerRef={viewerRef}
        labels={labels}
        interactive={isActive}
        selectedId={selectedLabelId}
        draggingId={draggingLabelId}
        onLabelPointerDown={handleLayerPointerDown}
      />

      {isActive && (
        <div
          className="fixed z-[1003] w-80 rounded-lg border border-white/15 bg-black/80 p-4 text-white shadow-2xl"
          style={{
            top: anchorRef?.current
              ? anchorRef.current.getBoundingClientRect().bottom + 12
              : 76,
            right: 16,
          }}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Asset labels</h3>
            <button
              className="text-white/50 hover:text-white"
              onClick={onClose}
              aria-label="Close label tool"
            >
              x
            </button>
          </div>

          {step === 'capturing' && (
            <div className="space-y-3">
              <p className="text-sm text-white/70">Click a point in the scene to anchor a label.</p>
              {labels.length > 0 && (
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-white/10 bg-white/5 p-1">
                  {labels.map((label) => (
                    <button
                      key={label.id}
                      className="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm text-white/85 hover:bg-white/10"
                      onClick={() => selectLabel(label)}
                    >
                      <span className="truncate">{label.text}</span>
                      <span className="ml-2 shrink-0 font-mono text-[10px] text-white/35">
                        {label.worldPos.x.toFixed(1)}, {label.worldPos.y.toFixed(1)}, {label.worldPos.z.toFixed(1)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'editing' && (
            <div className="space-y-3">
              <label className="block text-xs font-medium text-white/50">Label text</label>
              <input
                value={draftText}
                onChange={(event) => setDraftText(event.target.value)}
                autoFocus
                className="w-full rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#0CB4E4]"
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === 'Enter') void saveLabel();
                }}
              />
              <div className="flex gap-2">
                <button
                  className="flex-1 rounded-md bg-[#0CB4E4] px-3 py-2 text-sm font-semibold text-black"
                  disabled={!draftText.trim()}
                  onClick={() => void saveLabel()}
                >
                  Place label
                </button>
                <button
                  className="rounded-md border border-white/15 px-3 py-2 text-sm text-white/70"
                  onClick={() => {
                    setPendingPoint(null);
                    setStep('capturing');
                  }}
                >
                  Pick again
                </button>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="space-y-3">
              {selectedLabelId ? (
                <>
                  <label className="block text-xs font-medium text-white/50">Selected label</label>
                  <input
                    value={editText}
                    onChange={(event) => setEditText(event.target.value)}
                    className="w-full rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#0CB4E4]"
                    onKeyDown={(event) => {
                      event.stopPropagation();
                      if (event.key === 'Enter') void updateSelectedText();
                    }}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      className="rounded-md bg-[#0CB4E4] px-3 py-2 text-sm font-semibold text-black"
                      onClick={() => void updateSelectedText()}
                    >
                      Save
                    </button>
                    <button
                      className="rounded-md border border-white/15 px-3 py-2 text-sm text-white/70"
                      onClick={() => {
                        setSelectedLabelId(null);
                        setEditText('');
                      }}
                    >
                      Clear
                    </button>
                    <button
                      className="rounded-md border border-red-400/40 px-3 py-2 text-sm text-red-200"
                      onClick={() => void deleteSelectedLabel()}
                    >
                      Delete
                    </button>
                  </div>
                  <p className="text-xs text-white/45">Drag the label in the scene to reposition it.</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-white/70">Drag labels in the scene to reposition them. Click a label to edit or delete it.</p>
                  {labels.length > 0 && (
                    <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-white/10 bg-white/5 p-1">
                      {labels.map((label) => (
                        <button
                          key={label.id}
                          className="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm text-white/85 hover:bg-white/10"
                          onClick={() => selectLabel(label)}
                        >
                          <span className="truncate">{label.text}</span>
                          <span className="ml-2 shrink-0 font-mono text-[10px] text-white/35">
                            {label.worldPos.x.toFixed(1)}, {label.worldPos.y.toFixed(1)}, {label.worldPos.z.toFixed(1)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              <button
                className="w-full rounded-md bg-white/10 px-3 py-2 text-sm font-semibold text-white"
                onClick={() => {
                  setSelectedLabelId(null);
                  setEditText('');
                  setStep('capturing');
                }}
              >
                Add another label
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default LabelTool;
